import {computed, defineComponent, type PropType, ref, watch} from "vue";
import {uniq} from "lodash-es";
import {
    type IMessageQueueCountDto,
    MessageQueueCountDto,
    ServiceHealthStatus
} from "@/api/enterprise-gateway-api.proxy.ts";
import {AdminServiceDetail, AdminSessionDetail, AdminSessions} from "@/app/admin/admin-routes.ts";
import {useRouter} from "vue-router";

const MAX_QUEUE_SIZE = 10;
const HIGH_LOAD_THRESHOLD = 100;
const DEAD_LETTER_THRESHOLD = 10;

enum QueueState {
    ServiceDown = 'service-down',
    DeadLetter = 'dead-letter',
    HighLoad = 'high',
    Normal = 'normal',
    None = 'none'
}

export default defineComponent({
    name: 'AegPerformanceData',
    props: {
        service: {
            type: Object as PropType<IMessageQueueCountDto>,
            required: true
        },
        healthCheckOnly: {
            type: Boolean,
            default: false,
            required: false
        },
        filter: {
            type: String,
            default: '',
            required: false
        },
        counterOnly: {
            type: Boolean,
            default: false,
            required: false
        }
    },
    setup(props) {
        const router = useRouter();
        const previousActiveMessagesCounts = ref<number[]>([]);
        const previousDeadLettersCounts = ref<number[]>([]);
        const hasHighLoad = ref<boolean>(false);
        const hasAscendingDeadLetters = ref<boolean>(false);
        const serviceInfo = ref<IMessageQueueCountDto | undefined>(undefined);

        const healthCheckOnly = computed<boolean>(() => {
            return props.healthCheckOnly;
        });

        const counterOnly = computed<boolean>(() => {
            return props.counterOnly;
        });

        const isMessageCountIncreasing = (messageCount: number[]) => {
            const uniqueArray = uniq(messageCount);
            if (uniqueArray.length == 1) {
                return false;
            }
            for (let i = 0; i < uniqueArray.length - 1; i++) {
                if (uniqueArray[i] > uniqueArray[i + 1]) {
                    return false;
                }
            }
            return true; // All pairs are in increasing order
        }

        const buildAdminFilterSessionsQueryString = (filter: string) => {
            return filter === 'topup'
                ? { state: 'Submitted', type: 'TopUp' }
                : { state: filter, type: 'Payment'  };
        };

        const gotoSessions = async () => {
            const query = buildAdminFilterSessionsQueryString(props.filter);
            await router.push({ name: AdminSessions.name, query });
        };


        const queueChanged = (info: IMessageQueueCountDto) => {
            //Keep track of the last 10 numbers, if this is increasing and the total count is more then 100 the load is high on the queue
            if (previousActiveMessagesCounts.value.length >= MAX_QUEUE_SIZE) {
                previousActiveMessagesCounts.value.shift();
            }

            previousActiveMessagesCounts.value.push(info.activeCount)
            hasHighLoad.value = info.activeCount > HIGH_LOAD_THRESHOLD && isMessageCountIncreasing(previousActiveMessagesCounts.value);

            if (previousDeadLettersCounts.value.length >= MAX_QUEUE_SIZE) {
                previousDeadLettersCounts.value.shift();
            }

            previousDeadLettersCounts.value.push(info.errorCount)
            hasAscendingDeadLetters.value = info.errorCount >= DEAD_LETTER_THRESHOLD || isMessageCountIncreasing(previousDeadLettersCounts.value);

            serviceInfo.value = info;
        };

        const isServiceDown = (serviceInfo: IMessageQueueCountDto): boolean => {
            return serviceInfo && serviceInfo.serviceHealth.status === ServiceHealthStatus.Unhealthy;
        }

        const queueCssClass = (serviceInfo: IMessageQueueCountDto | undefined): readonly string[] => {
            if (!serviceInfo) {
                return [];
            }

            const defaultCssClasses = ['vl-alert', 'aeg-queue'] as const;
            const stateToClassMap: Record<QueueState, readonly string[]> = {
                [QueueState.ServiceDown]: [...defaultCssClasses, 'aeg-queue--service-down'],
                [QueueState.DeadLetter]: [...defaultCssClasses, 'aeg-queue--dead-letter'],
                [QueueState.HighLoad]: [...defaultCssClasses, 'aeg-queue--high'],
                [QueueState.Normal]: [...defaultCssClasses, 'aeg-queue--normal'],
                [QueueState.None]: [...defaultCssClasses, 'aeg-queue--none']
            } as const;


            if (isServiceDown(serviceInfo)) {
                return stateToClassMap[QueueState.ServiceDown];
            }

            if(healthCheckOnly.value) {
                return stateToClassMap[QueueState.Normal];
            }

            if (hasAscendingDeadLetters.value) {
                return stateToClassMap[QueueState.DeadLetter];
            }
            if (hasHighLoad.value) {
                return stateToClassMap[QueueState.HighLoad];
            }
            if (serviceInfo.activeCount > 0) {
                return stateToClassMap[QueueState.Normal];
            }

            return stateToClassMap[QueueState.None];
        };


        watch(() => props.service, queueChanged, {immediate: true, deep: true});

        return {
            routeAdminServiceDetailName: AdminServiceDetail.name,
            serviceInfo,
            healthCheckOnly,
            counterOnly,
            gotoSessions,
            isServiceDown,
            queueCssClass,
        };
    }
});
